import { SmsCodeService } from './sms-code.service';
import { ConfigService } from './config.service';
import { TeamPlan } from '../models/team-plan.model';
import { User } from './auth.service';
import { AuthService } from './auth.service';
import { Reply } from '../models/reply.model';
import { Member } from '../models/member.model';
import { FirebaseService } from './firebase.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Team } from '../models/team.model';
import { Injectable, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { Message } from '../models/message.model';
import { Recipient } from '../models/recipient.model';
import { Notification } from '../models/notifications.model';
import { interval } from 'rxjs';
import { take } from 'rxjs/operators';
import { MessagesStatus } from '../models/messages-status.model';
import { MessagesPageRoutingModule } from '../pages/messages/messages-routing.module';
import firebase from 'firebase/app';
import 'firebase/firestore';
import moment from 'moment';
import { CrashService } from './crash.service';
import { AnalyticsService } from './analytics.service';
import { Router } from '@angular/router';

/*

This service uses the following naming convention:

someEntityAdd - Tells the database to add a new doc
someEntityUpdate - Tells the database to save doc changes
someEntityDelete - Tells the database to delete a doc
someEntityGet - Gets related docs from the database

someEntitySet - Sets the local value of the item within this
TeamsService. Should be called after a successful database
write/read by any of the above calls. Should be followed by the
related broadcast call below to let other pages know about the
changes in service values.

someEntityBroadcast - tells other pages about the changes from
calls to ...Set().

By separating Set from Broadcast it allows this service to update
any number of related values before letting other pages know about
those changes.

*/

@Injectable({
  providedIn: 'root',
})
export class TeamsService implements OnDestroy {
  // Make sure we know who the user is
  userDataSubscription: Subscription;
  userData: User = null;

  // Provides subscribing pages with the list active teams
  private teamsSubject = new BehaviorSubject(null);
  teams$ = this.teamsSubject.asObservable();
  private teamsSubscription: Subscription;
  teams: Team[] = null;

  // Provides subscribing pages with the list archived teams
  private archivedTeamsSubject = new BehaviorSubject(null);
  archivedTeams$ = this.archivedTeamsSubject.asObservable();
  private archivedTeamsSubscription: Subscription;
  archivedTeams: Team[] = null;

  // Provides subscribing pages with the active team
  private teamSubject = new BehaviorSubject(null);
  team$ = this.teamSubject.asObservable();
  private teamSubscription: Subscription;
  team: Team = null;

  // Provides subscribing pages with ID's of the queued and latest messages
  private messagesStatusSubject = new BehaviorSubject(null);
  messagesStatus$ = this.messagesStatusSubject.asObservable();
  private messagesStatusSubscription: Subscription;
  messagesStatus: any = { latest: null, queued: null };

  // Provides subscribing pages with the details of any queued message
  private queuedMessageSubject = new BehaviorSubject(null);
  queuedMessage$ = this.queuedMessageSubject.asObservable();
  private queuedMessageSubscription: Subscription;
  queuedMessage: Message = null; // Current, if any

  // Provides subscribing pages with the details of the latest message
  private latestMessageSubject = new BehaviorSubject(null);
  latestMessage$ = this.latestMessageSubject.asObservable();
  private latestMessageSubscription: Subscription;
  latestMessage: Message = null; // Current, if any

  // Provides subscribing pages with the recipients of the latest message
  private messageRecipientsSubject = new BehaviorSubject(null);
  messageRecipients$ = this.messageRecipientsSubject.asObservable();
  private messageRecipientsSubscription: Subscription;
  messageRecipients: Recipient[] = null;

  // Provides subscribing pages with a list of messages for the active team
  private teamMessagesSubject = new BehaviorSubject(null);
  teamMessages$ = this.teamMessagesSubject.asObservable();
  private teamMessagesSubscription: Subscription;
  teamMessages: Message[] = null;

  // Provides subscribing pages with a list of members for the active team
  private teamMembersSubject = new BehaviorSubject(null);
  teamMembers$ = this.teamMembersSubject.asObservable();
  private teamMembersSubscription: Subscription;
  teamMembers: Member[] = null;

  // Provides subscribing pages with the selected team member, if any
  private teamMemberSubject = new BehaviorSubject(null);
  teamMember$ = this.teamMemberSubject.asObservable();
  private teamMemberSubscription: Subscription;
  teamMember: Member = null;

  // Provides subscribing pages with a list of the staff for the active team
  private teamStaffSubject = new BehaviorSubject(null);
  teamStaff$ = this.teamStaffSubject.asObservable();
  private teamStaffSubscription: Subscription;
  teamStaff: Member[] = null;

  // Provides subscribing pages with a list of members for the active team
  private teamMembersFormerSubject = new BehaviorSubject(null);
  teamMembersFormer$ = this.teamMembersFormerSubject.asObservable();
  private teamMembersFormerSubscription: Subscription;
  teamMembersFormer: Member[] = null;

  // Provides subscribing pages with a list of the staff for the active team
  private teamStaffFormerSubject = new BehaviorSubject(null);
  teamStaffFormer$ = this.teamStaffFormerSubject.asObservable();
  private teamStaffFormerSubscription: Subscription;
  teamStaffFormer: Member[] = null;

  // Provides subscribing pages with a list of replies for the latest message
  private messageRepliesSubject = new BehaviorSubject(null);
  messageReplies$ = this.messageRepliesSubject.asObservable();
  private messageRepliesSubscription: Subscription;
  messageReplies: Reply[] = null;

  // Provide subscribing page with list of all available numbers
  private numberPoolSubject = new BehaviorSubject(null);
  numberPool$ = this.numberPoolSubject.asObservable();
  private numberPoolSubscription: Subscription;
  numberPool: any[] = null;

  // Provide subscribing page with list of all notifications
  private notificationSubject = new BehaviorSubject(null);
  notification$ = this.notificationSubject.asObservable();
  private notificationSubscription: Subscription;
  notification: Notification[] = null;

  // Provide subscribing page with list of all users team-staff
  private userStaffSubject = new BehaviorSubject(null);
  userStaff$ = this.userStaffSubject.asObservable();
  private userStaffSubscription: Subscription;
  userStaff: any[] = null;

  constructor(
    private analytics: AnalyticsService,
    private crash: CrashService,
    public fbs: FirebaseService,
    private sms: SmsCodeService,
    private auth: AuthService,
    private router: Router,
    private config: ConfigService
  ) {
    this.userDataSubscription = this.auth.userData$.subscribe(
      async (userData: User) => {
        console.log(`team service, user data changed`, userData);
        if (!userData.uid) {
          console.log('have no uid yet');
          this.teamsDataClear();
        } else {
          console.log(`have a uid, get their teams info`, userData);
          this.userData = userData;
          // Get everything we know about this user's teams
          this.teamsGet();
          this.archivedTeamsGet();
          if (this.userData.currentTeamId) {
            const teamDoc = await this.fbs.afs
              .doc<Team>(`teams/${this.userData.currentTeamId}`)
              .get()
              .toPromise();
            // console.log('currentTeamDocRef');
            // console.log(teamDoc);
            if (teamDoc.exists) {
            this.teamGet(this.userData.currentTeamId);
            } else {
              // console.log(
              //   `current team ${this.userData.currentTeamId} does not exist`
              // );
              this.teamClear();
              this.router.navigate(['/teams']);
            }
          } else {
            this.teamClear();
          }
        }
      },
      (error: any) => {
        this.crash.subscription('teams-service', 'userDataSubscription', error);
      }
    );
  }

  ngOnDestroy() {
    console.log('Teams Service destroyed');
    this.teamClearSubscriptions();
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
  }

  disconnectUser() {
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
  }

  teamClearSubscriptions() {
    if (this.teamSubscription) {
      this.teamSubscription.unsubscribe();
    }
    if (this.messagesStatusSubscription) {
      this.messagesStatusSubscription.unsubscribe();
    }
    if (this.latestMessageSubscription) {
      this.latestMessageSubscription.unsubscribe();
    }
    if (this.queuedMessageSubscription) {
      this.queuedMessageSubscription.unsubscribe();
    }
    if (this.messageRecipientsSubscription) {
      this.messageRecipientsSubscription.unsubscribe();
    }
    if (this.teamMembersSubscription) {
      this.teamMembersSubscription.unsubscribe();
    }
    if (this.teamStaffSubscription) {
      this.teamStaffSubscription.unsubscribe();
    }
    if (this.teamMessagesSubscription) {
      this.teamMessagesSubscription.unsubscribe();
    }
    if (this.messageRepliesSubscription) {
      this.messageRepliesSubscription.unsubscribe();
    }
    if (this.teamMemberSubscription) {
      this.teamMemberSubscription.unsubscribe();
    }
    if (this.teamMembersFormerSubscription) {
      this.teamMembersFormerSubscription.unsubscribe();
    }
    if (this.teamStaffFormerSubscription) {
      this.teamStaffFormerSubscription.unsubscribe();
    }
    if (this.teamsSubscription) {
      this.teamsSubscription.unsubscribe();
    }
    if (this.archivedTeamsSubscription) {
      this.archivedTeamsSubscription.unsubscribe();
    }
    if (this.numberPoolSubscription) {
      this.numberPoolSubscription.unsubscribe();
    }
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  teamsDataClear() {
    this.teamsSet(null);
    this.archivedTeamsSet(null);
    this.teamSet(null);
    this.messagesStatusSet({ latest: null, queued: null });
    this.queuedMessageSet(null);
    this.latestMessageSet(null);
    this.messageRecipientsSet(null);
    this.teamMessagesSet(null);
    this.teamMembersSet(null);
    this.teamMemberSet(null);
    this.teamStaffSet(null);
    this.messageRepliesSet(null);
    this.notificationSet(null);
    this.teamClearSubscriptions();
  }

  teamsGet() {
    this.teamsSubscription = this.fbs
      .teamsWithIds$('teams', (ref) =>
        ref.where('teamArchived', '==', false).where('teamDeleted', '==', false)
      )
      .subscribe(
        (teams) => {
          if (teams) {
            console.log(`Teams service says there are ${teams.length} teams `);
            this.teamsSet([...teams]);
          } else {
            console.log('');
            this.crash.query(
              'teams-service',
              'teamsGet() got no teams response'
            );
          }
        },
        (error: any) => {
          this.crash.subscription('teams-service', 'teamsSubscription', error);
        }
      );
  }

  teamMessagesGet(teamId) {
    this.teamMessagesSubscription = this.fbs
      .messagesWithIds$('team-messages', (ref) =>
        ref.where('messageTeamId', '==', teamId).orderBy('messageCreatedAt')
      )
      .subscribe(
        (messages) => {
          if (messages) {
            console.log('Team messages:', messages);
            this.teamMessagesSet([...messages]);
          } else {
            console.log('');
            this.crash.query(
              'teams-service',
              'teamMessagesGet() got no teams response'
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'teamMessagesSubscription',
            error
          );
        }
      );
  }

  messageRepliesGet(teamId) {
    this.messageRepliesSubscription = this.fbs
      .repliesWithIds$('message-replies', (ref) =>
        ref.where('replyTeamId', '==', teamId).orderBy('replyCreatedAt')
      )
      .subscribe(
        (replies) => {
          if (replies) {
            console.log('Messages replies:', replies);
            this.messageRepliesSet([...replies]);
          } else {
            console.log('');
            this.crash.query(
              'teams-service',
              'messageRepliesGet() got no teams response'
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'messageRepliesSubscription',
            error
          );
        }
      );
  }

  archivedTeamsGet() {
    this.archivedTeamsSubscription = this.fbs
      .teamsWithIds$('teams', (ref) => ref.where('teamArchived', '==', true))
      .subscribe(
        (teams) => {
          if (teams) {
            console.log('Archived user teams are:', teams);
            this.archivedTeamsSet([...teams]);
          } else {
            console.log('');
            this.crash.query(
              'teams-service',
              'archivedTeamsGet() got no teams response'
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'archivedTeamsSubscription',
            error
          );
        }
      );
  }

  async teamActivate(teamId) {
    console.log(`teamActivate() - make team ${teamId} the current team`);
    if (teamId && this.team && this.team.teamId === teamId) {
      console.log(`teamActivate() - team ${teamId} already the current team`);
      return;
    }

    // Remember it at the user level
    console.log(`teamActivate() - remember current team ${teamId}`);
    this.auth.updateUser({ currentTeamId: teamId });

    // Forget about it
    this.teamClearSubscriptions();
    await this.teamClear();

    // Remember it at the service level and broadcast it
    if (teamId) {
      console.log(`teamActivate() - get current team ${teamId} details`);
      await this.teamGet(teamId);
    } else {
      console.log(`teamActivate() - clear current team data`);
    }
  }

  async teamClear() {
    if (this.teamSubscription) {
      this.teamSubscription.unsubscribe();
    }
    this.teamSet(null, true);
    this.messageRecipientsGet(null);
    this.teamMessagesGet(null);
    this.teamMembersGet(null);
    this.teamMemberGet(null);
    this.teamStaffGet(null);
    this.messageRepliesGet(null);
  }

  async teamGet(teamId) {
    console.log(`teamGet(${teamId})`);

    // Avoid multiple subscriptions
    if (this.teamSubscription) {
      this.teamSubscription.unsubscribe();
    }

    // Subscribe to changes for the team
    this.teamSubscription = await this.fbs.afs
      .doc<Team>(`teams/${teamId}`)
      .valueChanges()
      .subscribe(
        async (team) => {
          if (team) {
            team.teamId = teamId;
            console.log(`teamGet() - got team update`);
            // EXPIRATION
            const expired = moment()
              .utc()
              .isAfter(moment(team.teamPlan.expires));
            if (expired && !team.teamPlan.expired) {
              console.log(
                `teamGet() - plan expiration is past - expire it`,
                expired
              );
              this.teamExpire(team);
            } else {
              // Grab status before team update occurs
              const sameTeam =
                this.team && team.teamId === this.team.teamId ? true : false;
              // Remember it at the service level and broadcast latest data
              this.teamSet(team);
              this.messagesStatusSet(team.messagesStatus);
              // Activate all related info
              if (team.messagesStatus) {
                this.queuedMessageActivate(team.messagesStatus.queued);
                this.latestMessageActivate(team.messagesStatus.latest);
              } else {
                this.queuedMessageActivate(null);
                this.latestMessageActivate(null);
              }
              if (!sameTeam) {
                this.teamMembersGet(teamId);
                this.teamStaffGet(teamId);
              }
            }
          } else {
            console.log('have no current team');
          }
        },
        (error: any) => {
          this.crash.subscription('teams-service', 'teamSubscription', error);
        }
      );
  }

  teamMembersGet(teamId) {
    if (!teamId) {
      this.teamMembersSet(null);
      return;
    }
    console.log(`teamMembersGet(${teamId})`);
    this.teamMembersSubscription = this.fbs
      .membersWithIds$(`team-members`, (ref) =>
        ref
          .where('teamMemberTeamId', '==', teamId)
          .orderBy('teamMemberLastName', 'asc')
          .orderBy('teamMemberFirstName', 'asc')
      )
      .subscribe(
        (teamMembers) => {
          console.log('Team Members:', teamId, teamMembers);
          if (!teamMembers) {
            this.teamMembersSet(null);
          } else {
            this.teamMembersSet([...teamMembers]);
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'teamMembersSubscription',
            error
          );
        }
      );
  }

  async teamMemberGet(memberId) {
    console.log(`teamMemberGet(${memberId})`);
    if (memberId === null) {
      this.teamMemberSet(null);
    } else {
      this.teamMemberSubscription = await this.fbs.afs
        .doc<Member>(`/team-members/${memberId}`)
        .valueChanges()
        .subscribe(
          async (member) => {
            if (member) {
              console.log('found member', member);
              member.memberId = memberId;
              this.teamMemberSet(member);
            } else {
              this.crash.process(
                'teams-service',
                'teamMemberGet',
                `Cannot find member`,
                { memberId }
              );
            }
          },
          (error: any) => {
            this.crash.subscription(
              'teams-service',
              'teamMemberSubscription',
              error
            );
          }
        );
    }
  }

  async latestMessageActivate(messageId) {
    console.log(`latestMessageActivate(${messageId})`);
    if (messageId === null) {
      // Reset to no latest message
      this.latestMessageSet(null);
    } else {
      // Subscribe to changes for the message
      this.latestMessageSubscription = await this.fbs.afs
        .doc<Message>(`/team-messages/${messageId}`)
        .valueChanges()
        .subscribe(
          async (message) => {
            if (message) {
              console.log('found latest message', message);
              message.messageId = messageId;
              // Remember it at the team level, if not already set
              // if (this.team.messagesStatus.latest !== messageId) {
              //   const messagesStatus: MessagesStatus = this.team.messagesStatus;
              //   messagesStatus.latest = message;
              //   await this.teamUpdate(this.team.teamId, {
              //     messagesStatus,
              //   });
              //   this.messagesStatusSet(messagesStatus);
              // }
              // Remember it at the service level and broadcast it
              this.latestMessageSet(message);
            } else {
              this.crash.process(
                'teams-service',
                'latestMessageActivate',
                `Cannot find message`,
                { messageId }
              );
            }
          },
          (error: any) => {
            this.crash.subscription(
              'teams-service',
              'latestMessageSubscription',
              error
            );
          }
        );
    }
  }

  async queuedMessageActivate(messageId) {
    console.log(`queuedMessageActivate(${messageId})`);
    if (messageId === null) {
      // Reset to no queued message
      this.queuedMessageSet(null);
    } else {
      // Subscribe to changes for the message
      this.queuedMessageSubscription = await this.fbs.afs
        .doc<Message>(`/team-messages/${messageId}`)
        .valueChanges()
        .subscribe(
          async (message) => {
            if (message) {
              console.log('found queued message', message);
              message.messageId = messageId;
              // // Remember it at the team level, if not already set
              // if (this.team.messagesStatus.queued !== messageId) {
              //   const messagesStatus: MessagesStatus = this.team.messagesStatus;
              //   messagesStatus.queued = messageId;
              //   await this.teamUpdate(this.team.teamId, {
              //     messagesStatus,
              //   });
              //   this.messagesStatusSet(messagesStatus);
              // }
              // Remember it at the service level and broadcast it
              this.queuedMessageSet(message);
            } else {
              this.crash.process(
                'teams-service',
                'queuedMessageActivate',
                `Cannot find queued message`,
                { messageId }
              );
            }
          },
          (error: any) => {
            this.crash.subscription(
              'teams-service',
              'queuedMessageSubscription',
              error
            );
          }
        );
    }
  }

  messageRecipientsGet(messageId) {
    if (!messageId) {
      this.messageRecipientsSet([]);
      return;
    }
    console.log(`messageRecipientsGet(${messageId})`);
    this.messageRecipientsSubscription = this.fbs
      .recipientsWithIds$(`/team-messages/${messageId}/recipients`)
      .subscribe(
        (recipients) => {
          if (recipients) {
            console.log('Message Recipients found', messageId, recipients);
            this.messageRecipientsSet([...recipients]);
          } else {
            this.crash.process(
              'teams-service',
              'messageRecipientsGet',
              `Cannot find queued message recipients`,
              { messageId }
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'messageRecipientsSubscription',
            error
          );
          // debugger;
        }
      );
  }

  async messageQueue(message, recipients) {
    // Add message to firestore
    const messageDoc = await this.messageAdd(message);

    // Add recipients to subcollection
    await this.messageAddRecipients(messageDoc.id, recipients);

    await this.messageMemberUpdate(recipients);

    // Gather them and broadcast
    this.messageRecipientsGet(messageDoc.id);

    // Make this the queued message
    this.queuedMessageActivate(messageDoc.id);

    // Pretend to send it; TODO_MH: remove this and send from a cloud function
    this.messageSendMock(messageDoc.id);
  }

  messagesStatusSet(messagesStatus, broadcast = true) {
    this.messagesStatus = messagesStatus;
    if (broadcast) {
      this.messagesStatusBroadcast(messagesStatus);
    }
  }

  messagesStatusBroadcast(messagesStatus) {
    this.messagesStatusSubject.next(messagesStatus);
  }

  mockRecipientReply(teamPhoneFull, memberId, msg) {
    firebase
      .firestore()
      .collection('team-members')
      .doc(memberId)
      .get()
      .then((member) => {
        const memberData = member.data();
        this.teamMemberMockResponse(
          memberData.teamMemberMobileNumber.full,
          teamPhoneFull,
          msg
        );
      })
      .catch((error) => {
        console.log('mockRecipientReply error', error);
      });
  }

  messageSendMock(messageId) {
    console.log('message will be sent by server');
    interval(500)
      .pipe(take(1))
      .subscribe(
        () => {
          this.messageUpdate(messageId, {
            messageStatus: 'queued',
          });
          const messagesStatus = {
            queued: null,
            latest: messageId,
          };
          this.teamUpdate(this.team.teamId, {
            messagesStatus,
          }).then(() => {
            console.log(`TEAM UPDATED`);
            this.queuedMessageSet(null);
          });
        },
        (e) => console.log(`error sending message`, e),
        () => {
          console.log('message sent');
        }
      );
  }

  messageReplySendMock(replyId, messageId, memberId, replyText, replyType) {
    console.log('reply will be sent by server');
    interval(500)
      .pipe(take(1))
      .subscribe(
        async () => {
          this.messageReplyUpdate(replyId, {
            replyStatus: 'sent',
          });

          await this.recipientLatestReplyUpdate(
            replyId,
            messageId,
            memberId,
            replyText,
            memberId,
            replyType
          );

          this.messageRecipientsGet(messageId);
        },
        (e) => console.log(`error sending reply`, e),
        () => {
          console.log('reply sent');
        }
      );
  }

  replyReplySendMock(
    replyId,
    messageId,
    memberId,
    replyText,
    staffId,
    replyType
  ) {
    console.log('reply will be sent by server');
    interval(500)
      .pipe(take(1))
      .subscribe(
        async () => {
          this.messageReplyUpdate(replyId, {
            replyStatus: 'sent',
          });

          await this.recipientLatestReplyUpdate(
            replyId,
            messageId,
            memberId,
            replyText,
            staffId,
            replyType
          );

          this.messageRecipientsGet(messageId);
        },
        (e) => console.log(`error sending reply`, e),
        () => {
          console.log('reply sent');
        }
      );
  }

  async replyByStaff(data) {
    const teamId = data.teamId;
    const messageId = data.messageId;
    const memberId = data.memberId;
    const senderId = data.senderId;
    const replyType = data.replyType;

    const reply: Reply = {
      replyStatus: 'queued',
      replyTeamId: teamId,
      replyMessageId: messageId,
      replySenderId: data.senderId,
      replySenderName: data.senderName,
      replyText: data.replyText,
      // replyCreatedAt: Assigned by server,
      // replySentAt: Assigned by server,
      replySentTo: memberId,
      replyType,
      media: data.media,
    };
    this.replyReplyQueue(reply, messageId, memberId, senderId, replyType);
  }

  async replyReplyQueue(reply, messageId, memberId, senderId, replyType) {
    // Add reply to firestore
    const replyDoc = await this.messageReplyAdd(reply);

    // Pretend to send it; TODO_MH: remove this and send from a cloud function
    this.replyReplySendMock(
      replyDoc.id,
      messageId,
      memberId,
      reply.replyText,
      senderId,
      replyType
    );
  }

  async messageReplyQueue(reply, messageId, memberId) {
    // Add reply to firestore
    const replyDoc = await this.messageReplyAdd(reply);

    // Pretend to send it; TODO_MH: remove this and send from a cloud function
    this.messageReplySendMock(
      replyDoc.id,
      messageId,
      memberId,
      reply.replyText,
      reply.replyType
    );
  }

  teamStaffGet(teamId) {
    console.log(`teamStaffGet(${teamId})`);
    this.teamStaffSubscription = this.fbs
      .membersWithIds$(`team-staff`, (ref) =>
        ref
          .where('teamStaffTeamId', '==', teamId)
          .orderBy('teamStaffLastName', 'asc')
          .orderBy('teamStaffFirstName', 'asc')
      )
      .subscribe(
        (teamStaff) => {
          if (!teamStaff) {
            teamStaff = [];
          }
          console.log('Team Staff:', teamId, teamStaff);
          this.teamStaffSet([...teamStaff]);
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'teamStaffSubscription',
            error
          );
        }
      );
  }

  teamsSet(teams) {
    console.log('teamsSet()', teams);
    this.teams = teams;
    this.teamsSubject.next(teams);
  }

  teamSet(team: Team, broadcast = true) {
    if (!team) {
      console.log('setTeam(null)');
      this.team = team;
      if (broadcast) {
        this.teamBroadcast(team);
      }
      return;
    }

    console.log('setTeam', team);
    const teamId = team.teamId;
    if (this.userData.currentTeamId !== teamId) {
      this.fbs.updateUser(this.userData.uid, { currentTeamId: teamId });
    }
    this.team = team;
    if (broadcast) {
      this.teamBroadcast(team);
    }
  }

  teamBroadcast(team) {
    this.teamSubject.next(team);
  }

  latestMessageSet(message, broadcast = true) {
    console.log('latestMessageSet', message);
    this.latestMessage = message;
    if (broadcast) {
      this.latestMessageBroadcast(message);
    }
  }

  latestMessageBroadcast(message) {
    this.latestMessageSubject.next(message);
  }

  queuedMessageSet(message, broadcast = true) {
    console.log('queuedMessageSet', message);
    this.queuedMessage = message;
    if (broadcast) {
      this.queuedMessageBroadcast(message);
    }
  }

  queuedMessageBroadcast(message) {
    this.queuedMessageSubject.next(message);
  }

  teamMembersSet(teamMembers, broadcast = true) {
    console.log('teamMembersSet', teamMembers);
    this.teamMembers = teamMembers;
    if (broadcast) {
      this.teamMembersBroadcast(teamMembers);
    }
  }

  teamMembersBroadcast(teamMembers) {
    this.teamMembersSubject.next(teamMembers);
  }

  teamMemberSet(teamMember, broadcast = true) {
    console.log('teamMemberSet', teamMember);
    this.teamMember = teamMember;
    if (broadcast) {
      this.teamMemberBroadcast(teamMember);
    }
  }

  teamMemberBroadcast(teamMember) {
    this.teamMemberSubject.next(teamMember);
  }

  teamMessagesSet(teamMessages, broadcast = true) {
    console.log('teamMessagesSet', teamMessages);
    this.teamMessages = teamMessages;
    if (broadcast) {
      this.teamMessagesBroadcast(teamMessages);
    }
  }

  teamMessagesBroadcast(teamMessages) {
    this.teamMessagesSubject.next(teamMessages);
  }

  messageRepliesSet(replies, broadcast = true) {
    console.log('messagesRepliesSet', replies);
    this.messageReplies = replies;
    if (broadcast) {
      this.messageRepliesBroadcast(replies);
    }
  }

  messageRepliesBroadcast(messageReplies) {
    this.messageRepliesSubject.next(messageReplies);
  }

  messageRecipientsSet(recipients, broadcast = true) {
    console.log('messageRecipientsSet', recipients);
    this.messageRecipients = recipients;
    if (broadcast) {
      this.messageRecipientsBroadcast(recipients);
    }
  }

  messageRecipientsBroadcast(recipients) {
    this.messageRecipientsSubject.next(recipients);
  }

  teamStaffSet(teamStaff, broadcast = true) {
    console.log('teamStaffSet', teamStaff);
    this.teamStaff = teamStaff;
    if (broadcast) {
      this.teamStaffBroadcast(teamStaff);
    }
  }

  teamStaffBroadcast(teamStaff) {
    this.teamStaffSubject.next(teamStaff);
  }

  archivedTeamsSet(teams, broadcast = true) {
    this.archivedTeams = teams;
    if (broadcast) {
      this.archivedTeamsBroadcast(teams);
    }
  }

  archivedTeamsBroadcast(teams) {
    this.archivedTeamsSubject.next(teams);
  }

  async teamAdd(data): Promise<firebase.firestore.DocumentReference> {
    console.log('teamAdd()', data);
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      data.teamPlan = {
        product: 'none',
        expires: null,
        expired: null,
      };
      data.teamAdminId = user.uid;
      data.teamStaff = [user.uid];
      const timestamp = this.fbs.timestamp;
      return this.fbs.collection('teams').add({
        ...data,
        teamUpdatedAt: timestamp,
        teamCreatedAt: timestamp,
      });
    } else {
      this.crash.process(
        'teams-service',
        'teamAdd',
        `Failed to determine user creating team`,
        data
      );
    }
  }

  async messageAdd(data): Promise<firebase.firestore.DocumentReference> {
    console.log('messageAdd()', data);
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      const timestamp = this.fbs.timestamp;
      return this.fbs.collection(`/team-messages`).add({
        // messageId: string (system assigned on creation),
        messageSenderId: user.uid,
        messageSenderName: data.messageSenderName,
        messageText: data.messageText,
        messagesStatus: 'queuing',
        messageTeamId: data.messageTeamId,
        messageType: data.messageType,
        media: data.media,
        messageCreatedAt: timestamp,
        // messageSentAt: timestamp (will assigned when sent)
        // messageSentTo: data.messageSentTo,
      });
    } else {
      this.crash.process(
        'teams-service',
        'messageAdd',
        `Failed to determine user creating team message`,
        data
      );
    }
  }

  async messageReplyAdd(data): Promise<firebase.firestore.DocumentReference> {
    console.log('messageReplyAdd()', data);
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      const timestamp = this.fbs.timestamp;
      return this.fbs.collection(`/message-replies`).add({
        // replyId: string (system assigned on creation),
        replySenderId: user.uid,
        replySenderName: data.replySenderName,
        replyType: data.replyType,
        replyText: data.replyText,
        replyStatus: 'queuing',
        replyTeamId: data.replyTeamId,
        replyMessageId: data.replyMessageId,
        replyCreatedAt: timestamp,
        // replySentAt: timestamp (will be assigned when sent)
        replySentTo: data.replySentTo,
        media: data.media ? data.media : [],
      });
    } else {
      this.crash.process(
        'teams-service',
        'messageReplyAdd',
        `Failed to determine user creating team message reply`,
        data
      );
    }
  }

  async messageRecipientAdd(messageDocId, recipient: Recipient) {
    console.log('add recipient', messageDocId, recipient);
    this.fbs
      .doc(`/team-messages/${messageDocId}/recipients/${recipient.memberId}`)
      .set({ latestReply: null });
  }

  async memberAdd(data): Promise<firebase.firestore.DocumentReference> {
    console.log('memberAdd()', data);
    const teamId = data.teamId;
    this.analytics.track('qt_member_add', { team_id: teamId });
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      this.analytics.track('qt_staff_add', { team_id: teamId });
      const timestamp = this.fbs.timestamp;
      return this.fbs.collection(`/team-members`).add({
        teamMemberTeamId: teamId,
        teamMemberStatus: 'invited',
        teamMemberFirstName: data.firstName,
        teamMemberLastName: data.lastName,
        teamMemberMobileNumber: data.mobileNumber,
        teamMemberUpdatedAt: timestamp,
        teamMemberCreatedAt: timestamp,
        teamMemberUpdatedBy: user.uid,
        teamMemberCreatedBy: user.uid,
        teamMemberCreatedByName: data.createdByName,
      });
    } else {
      this.crash.process(
        'teams-service',
        'memberAdd',
        `Failed to determine user creating team member`,
        data
      );
    }
  }

  isMember(memberType, mobileNumber) {
    console.log('isMember');
    if (memberType === 'members') {
      return !!_.find(this.teamMembers, [
        'teamMemberMobileNumber.full',
        mobileNumber.full,
      ]);
    } else {
      return !!_.find(this.teamStaff, [
        'teamStaffMobileNumber.full',
        mobileNumber.full,
      ]);
    }
  }

  async staffAdd(data): Promise<firebase.firestore.DocumentReference> {
    console.log('staffAdd()', data);

    let status = 'invited';

    if (data.status) {
      status = data.status;
    }

    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      await this.teamStaffAdd(data.teamId, data.mobileNumber.full);

      const timestamp = this.fbs.timestamp;
      return this.fbs.collection(`/team-staff`).add({
        teamStaffTeamId: data.teamId,
        teamMemberUserId: data.mobileNumber.full,
        teamStaffStatus: status,
        teamStaffFirstName: data.firstName,
        teamStaffLastName: data.lastName,
        teamStaffMobileNumber: data.mobileNumber,
        teamStaffUpdatedAt: timestamp,
        teamStaffCreatedAt: timestamp,
        teamStaffUpdatedBy: user.uid,
        teamStaffCreatedBy: user.uid,
        teamStaffCreatedByName: data.createdByName,
      });
    } else {
      console.log('Failed to find uid creating team staff');
      this.crash.process(
        'teams-service',
        'staffAdd',
        `Failed to determine user creating team staff`,
        data
      );
    }
  }

  teamStaffAdd(teamId, staffId) {
    this.fbs
      .collection('teams')
      .doc(teamId)
      .update({
        teamStaff: firebase.firestore.FieldValue.arrayUnion(staffId),
      });
  }

  memberRemove(member, memberType) {
    this.fbs.update(`/team-${memberType}/${member.memberId}`, {
      teamMemberStatus: 'removed',
    });
  }

  memberRestore(member, memberType) {
    this.fbs.update(`/team-${memberType}/${member.memberId}`, {
      teamMemberStatus: 'active',
    });
  }

  memberReinvite(member, memberType, teamId, messageId) {
    this.fbs.update(`/team-${memberType}/${member.memberId}`, {
      teamMemberStatus: 'reinvited',
    });

    this.replyByStaff({
      teamId: teamId,
      messageId: messageId,
      replyType: 'invite',
      memberId: member.memberId,
      replyText: 'Resent Invite',
      senderId: this.userData.uid,
      senderName: `${this.userData.firstName} ${this.userData.lastName}`,
    });
  }

  teamArchive(team: Team) {
    console.log('teamArchive()', team);
    if (team.teamId === this.userData.currentTeamId) {
      console.log('teamArchive() says archiving current team, clear it', team);
      this.teamActivate(null);
    } else {
      console.log(
        `teamArchive() says NOT archiving current team ${this.userData.currentTeamId}`,
        team
      );
    }
    console.log(
      'teamArchive() says marking user as having archived teams',
      team
    );
    console.log('teamArchive() says marking team as archived', team);
    this.fbs.update(`/teams/${team.teamId}`, { teamArchived: true });
    this.fbs.updateUser(this.userData.uid, {
      hasArchivedTeams: true,
    });
  }

  // EXPIRATION
  expiresFromNow(team: Team) {
    if (this.config.debugExpiration) {
      return moment(team.teamPlan.expires).diff(moment().utc(), 'minutes');
    } else {
      return moment(team.teamPlan.expires).diff(moment().utc(), 'days');
    }
  }

  teamExpire(team: Team) {
    const plan: TeamPlan = team.teamPlan;
    switch (plan.product) {
      case this.config.trialProduct:
        break;
      case this.config.quarterlyProduct:
        break;
      case this.config.annualProduct:
        break;
    }
    plan.expired = true;
    this.fbs.update(`/teams/${team.teamId}`, { teamPlan: plan });
    this.fbs.updateUser(this.userData.uid, {
      trial: { expires: this.userData.trial.expires, status: 'expired' },
    });
  }

  teamRestore(team: Team) {
    if (this.archivedTeams.length === 1) {
      this.fbs.updateUser(this.userData.uid, {
        hasArchivedTeams: false,
      });
    }
    this.fbs.update(`/teams/${team.teamId}`, { teamArchived: false });
  }

  teamDelete(team: Team) {
    if (this.archivedTeams.length === 1) {
      this.fbs.updateUser(this.userData.uid, {
        hasArchivedTeams: false,
      });
    }
    //this.fbs.delete(`/teams/${team.teamId}`);
    this.fbs.update(`/teams/${team.teamId}`, {
      teamOriginalAdminId: team.teamAdminId,
      teamAdminId: null,
      teamArchived: false,
      teamDeleted: true,
    });

    this.fbs.update(`/number-pool/${team.teamPhoneId}`, {
      numberHasTeam: false,
      numberTeamSid: null,
    });
  }

  teamGrant(team, uid) {
    this.fbs.update(`/teams/${team.teamId}`, { teamAdminId: uid });
  }

  teamMemberRemove(memberId, memberType) {
    this.fbs.update(`/team-${memberType}/${memberId}`, {
      teamMemberStatus: 'removed',
    });
  }

  teamRemoveMemberInvitation(memberId, memberType) {
    this.fbs.delete(`/team-${memberType}/${memberId}`);
  }

  teamMemberMockResponse(memberPhone, teamPhone, response) {
    console.log(
      `Mock response ${memberPhone} to team: ${teamPhone} Msg: ${response}`
    );
    this.sms.mockResponse(teamPhone, memberPhone, response);
  }

  memberDefault() {
    return {
      firstName: null,
      lastName: null,
      mobileNumber: {
        valid: false,
        countryTag: 'us',
        country: '',
        area: '',
        prefix: '',
        line: '',
        friendly: '',
        full: '',
      },
      trial: { status: 'none', expires: null },
      latestReply: null,
    };
  }

  async teamUpdate(teamId, changes) {
    this.fbs.update(`/teams/${teamId}`, changes);
  }

  async memberUpdate(member: Member, updates) {
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      const timestamp = this.fbs.timestamp;
      const changes = {
        teamMemberFirstName: updates.firstName,
        teamMemberLastName: updates.lastName,
        teamMemberMobileNumber: updates.mobileNumber,
        teamMemberUpdatedAt: timestamp,
        teamMemberUpdatedBy: user.uid,
      };
      this.fbs.update(`/team-members/${member.memberId}`, changes);
    } else {
      this.crash.process(
        'teams-service',
        'memberUpdate',
        `Failed to determine user updating team member`,
        updates
      );
    }
  }

  async staffUpdate(member: Member, updates) {
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      const timestamp = this.fbs.timestamp;
      const changes = {
        teamStaffFirstName: updates.firstName,
        teamStaffLastName: updates.lastName,
        teamStaffMobileNumber: updates.mobileNumber,
        teamStaffUpdatedAt: timestamp,
        teamStaffUpdatedBy: user.uid,
      };
      this.fbs.update(`/team-staff/${member.memberId}`, changes);
    } else {
      this.crash.process(
        'teams-service',
        'memberUpdate',
        `Failed to determine user updating team staff`,
        updates
      );
    }
  }

  async messageUpdate(messageId, changes) {
    this.fbs.update(`/team-messages/${messageId}`, changes);
  }

  async recipientLatestReplyUpdate(
    replyId,
    messageId,
    memberId,
    replyText,
    replySenderId,
    replyType
  ) {
    const replyTimestamp = this.fbs.timestamp;

    if (replyType === 'message') {
      // Increment the reply count
      const increment = firebase.firestore.FieldValue.increment(1);

      // Make sure the latest message knows of the reply for Messages page presentation
      this.fbs
        .doc(`/team-messages/${messageId}/recipients/${memberId}`)
        .update({
          'latestReply.replySenderId': replySenderId,
          'latestReply.replyType': replyType,
          'latestReply.replyCount': increment,
          'latestReply.replyTimestamp': replyTimestamp,
        });

      // Make sure the member knows of the reply for Inbox page presentation
      this.fbs.doc(`/team-members/${memberId}`).update({
        'latestReply.replySenderId': replySenderId,
        'latestReply.replyType': replyType,
        'latestReply.replyCount': increment,
        'latestReply.replyTimestamp': replyTimestamp,
      });
    } else {
      // Make sure the latest message knows of the reply for Messages page presentation
      this.fbs
        .doc(`/team-messages/${messageId}/recipients/${memberId}`)
        .update({
          'latestReply.replySenderId': replySenderId,
          'latestReply.replyType': replyType,
          'latestReply.replyTimestamp': replyTimestamp,
        });

      // Make sure the member knows of the reply for Inbox page presentation
      this.fbs.doc(`/team-members/${memberId}`).update({
        'latestReply.replySenderId': replySenderId,
        'latestReply.replyType': replyType,
        'latestReply.replyTimestamp': replyTimestamp,
      });
    }
  }

  async recipientLatestReplySet(
    replyId,
    messageId,
    memberId,
    replyText,
    replySenderId,
    replyType
  ) {
    const replyTimestamp = this.fbs.timestamp;

    if (replyType === 'message') {
      // Increment the reply count
      const increment = firebase.firestore.FieldValue.increment(1);

      // Make sure the latest message knows of the reply for Messages page presentation
      this.fbs
        .doc(`/team-messages/${messageId}/recipients/${memberId}`)
        .update({
          'latestReply.replySenderId': replySenderId,
          'latestReply.replyType': replyType,
          'latestReply.replyCount': increment,
          'latestReply.replyTimestamp': replyTimestamp,
        });

      // Make sure the member knows of the reply for Inbox page presentation
      this.fbs.doc(`/team-members/${memberId}`).update({
        'latestReply.replySenderId': replySenderId,
        'latestReply.replyType': replyType,
        'latestReply.replyCount': increment,
        'latestReply.replyTimestamp': replyTimestamp,
      });
    } else {
      // Make sure the latest message knows of the reply for Messages page presentation
      this.fbs
        .doc(`/team-messages/${messageId}/recipients/${memberId}`)
        .update({
          'latestReply.replySenderId': replySenderId,
          'latestReply.replyType': replyType,
          'latestReply.replyTimestamp': replyTimestamp,
        });

      // Make sure the member knows of the reply for Inbox page presentation
      this.fbs.doc(`/team-members/${memberId}`).update({
        'latestReply.replySenderId': replySenderId,
        'latestReply.replyType': replyType,
        'latestReply.replyTimestamp': replyTimestamp,
      });
    }
  }

  async messageReplyUpdate(replyId, changes) {
    this.fbs.update(`/message-replies/${replyId}`, changes);
  }

  async messageMemberUpdate(members) {
    if (members !== null) {
      members.forEach(async (member: Member) => {
        this.fbs
          .doc(`/team-members/${member.memberId}`)
          .update({ latestReply: null });
      });
    }
  }

  async messageAddRecipients(messageId, members) {
    if (members !== null) {
      members.forEach(async (member: Member) => {
        const recipient: Recipient = {
          memberId: member.memberId,
          latestReply: null,
        };
        await this.messageRecipientAdd(messageId, recipient);
      });
    }
  }

  /* notification */
  async notificationGet(teamsList) {
    console.log('notificationGet()');
    console.log(teamsList);
    this.notificationSubscription = this.fbs
      .notificationWithIds$('notifications', (ref) =>
        ref
          .where('notificationTeamId', 'in', teamsList)
          .orderBy('notificationCreatedAt')
      )
      .subscribe(
        (notifications) => {
          if (notifications) {
            console.log(notifications);
            this.notificationSet([...notifications]);
          } else {
            this.crash.query(
              'teams-service',
              'notificationGet() got no pool response'
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'notificationSubscription',
            error
          );
        }
      );
  }

  notificationSet(notifications) {
    console.log('notificationSet');
    this.notification = notifications;
    this.notificationSubject.next(notifications);
  }

  async notificationUpdate(notificationId, changes) {
    this.fbs.update(`/notifications/${notificationId}`, changes);
  }

  /* numberPool */
  numberPoolGet() {
    console.log('numberPoolGet()');
    this.numberPoolSubscription = this.fbs
      .numberPoolWithIds$('number-pool', (ref) =>
        ref.where('numberHasTeam', '==', false)
      )
      .subscribe(
        (numbers) => {
          if (numbers) {
            this.numberPoolSet([...numbers]);
          } else {
            this.crash.query(
              'teams-service',
              'numberPoolGet() got no pool response'
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'numberPoolSubscription',
            error
          );
        }
      );
  }

  numberPoolSet(numbers) {
    this.numberPool = numbers;
    this.numberPoolSubject.next(numbers);
  }

  async numberPoolUpdate(numberId, changes) {
    this.fbs.update(`/number-pool/${numberId}`, changes);
  }

  isTeamNameAvailable(name) {
    return !!!_.find(this.teams, ['teamName', name]);
  }

  hasPhoneOrURL(invite) {
    const phoneExp =
      /(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?/gim;
    const urlExp =
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&/=]*)/g;

    if (phoneExp.test(invite)) {
      return true;
    }

    if (urlExp.test(invite)) {
      return true;
    }

    return false;
  }

  /* userStaff - Syncing User to their Staff Record */
  userStaffGet(memberId) {
    console.log('userStaffGet');
    this.userStaffSubscription = this.fbs
      .userStaffWithIds$('team-staff', (ref) =>
        ref.where('teamMemberUserId', '==', memberId)
      )
      .subscribe(
        (staff) => {
          if (staff) {
            this.userStaffSet([...staff]);
          } else {
            this.crash.query(
              'teams-service',
              'userStaffGet() Failed to find staff member'
            );
          }
        },
        (error: any) => {
          this.crash.subscription(
            'teams-service',
            'userStaffSubscription',
            error
          );
        }
      );
  }

  userStaffSet(teams) {
    this.userStaff = teams;
    this.userStaffSubject.next(teams);
  }

  userStaffUpdate(staffId, changes) {
    let staffChanges: any = {};

    if (changes?.firstName) {
      staffChanges.teamStaffFirstName = changes.firstName;
    }

    if (changes?.lastName) {
      staffChanges.teamStaffLastName = changes.lastName;
    }

    this.fbs.update(`/team-staff/${staffId}`, staffChanges);
  }

  async mockTeamCreate(team) {
    await this.fbs.mockTeamCreate(team);
  }
}
